import { useState, useEffect, useMemo, useRef } from "react";
import { API } from "@/App";
import { toast } from "sonner";
import DaxLiveHeader from "@/components/DaxLiveHeader";
import {
  TrendUp,
  TrendDown,
  Trophy,
  Warning,
  Plus,
  DownloadSimple,
  UploadSimple,
  ArrowsClockwise,
  Pencil,
  Trash,
  X,
  Funnel,
  ArrowUp,
  ArrowDown,
  Lightning,
  Clock,
  Calendar,
  ChartBar,
  Target,
  MagnifyingGlass,
  Sparkle,
} from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine,
} from "recharts";

// ===== Color palette per the brief =====
const COLORS = {
  profit: "#1D9E75",   // green
  loss: "#D85A30",     // red/coral
  neutral: "#378ADD",  // blue
};

// ===== Bot type → coral / blue / teal badge =====
const BOT_BADGE = {
  scalp: { label: "Scalp", className: "bg-[#FF7F6E]/15 text-[#D85A30] border-[#FF7F6E]/40" },
  day:   { label: "Day",   className: "bg-[#378ADD]/15 text-[#1F65B0] border-[#378ADD]/40" },
  swing: { label: "Swing", className: "bg-[#14B8A6]/15 text-[#0F766E] border-[#14B8A6]/40" },
};

const fmtEur = (v) => {
  const n = Number(v) || 0;
  const sign = n < 0 ? "-" : (n > 0 ? "+" : "");
  return `${sign}${Math.abs(n).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
};
const fmtPts = (v) => {
  const n = Number(v) || 0;
  const sign = n < 0 ? "" : (n > 0 ? "+" : "");
  return `${sign}${n.toFixed(1)} pts`;
};
const fmtPrice = (v) => {
  if (v === null || v === undefined || v === "") return "-";
  return Number(v).toLocaleString("fr-FR", { minimumFractionDigits: 1, maximumFractionDigits: 2 });
};

// ===== Hero Stat Card =====
const StatCard = ({ icon: Icon, label, value, sub, accent = "neutral", testid }) => {
  const accentColor = accent === "profit" ? COLORS.profit : accent === "loss" ? COLORS.loss : COLORS.neutral;
  return (
    <Card className="rounded-sm border-slate-200 shadow-none hover:shadow-md transition-shadow" data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-body font-bold">{label}</span>
          <div
            className="w-7 h-7 rounded-sm flex items-center justify-center"
            style={{ background: `${accentColor}15`, color: accentColor }}
          >
            <Icon size={14} weight="bold" />
          </div>
        </div>
        <div className="font-data text-2xl font-bold tabular-nums" style={{ color: accentColor }}>
          {value}
        </div>
        {sub && <div className="text-xs text-slate-500 mt-1 font-body">{sub}</div>}
      </CardContent>
    </Card>
  );
};

// ===== Trade Entry Form Modal =====
const TradeFormDialog = ({ open, onClose, onSubmit, trade }) => {
  const isEdit = !!trade;
  const [form, setForm] = useState({
    trade_date: new Date().toISOString().slice(0, 10),
    trade_time: new Date().toTimeString().slice(0, 5),
    bot_type: "day",
    direction: "BUY",
    entry_price: "",
    stop_loss: "",
    take_profit_1: "",
    take_profit_2: "",
    take_profit_3: "",
    exit_price: "",
    lot_size: "0.2",
    duration_minutes: "",
    notes: "",
  });

  useEffect(() => {
    if (trade) {
      setForm({
        trade_date: trade.trade_date || "",
        trade_time: trade.trade_time || "",
        bot_type: trade.bot_type || "day",
        direction: trade.direction || "BUY",
        entry_price: trade.entry_price ?? "",
        stop_loss: trade.stop_loss ?? "",
        take_profit_1: trade.take_profit_1 ?? "",
        take_profit_2: trade.take_profit_2 ?? "",
        take_profit_3: trade.take_profit_3 ?? "",
        exit_price: trade.exit_price ?? "",
        lot_size: trade.lot_size ?? "0.2",
        duration_minutes: trade.duration_minutes ?? "",
        notes: trade.notes ?? "",
      });
    } else if (open) {
      setForm({
        trade_date: new Date().toISOString().slice(0, 10),
        trade_time: new Date().toTimeString().slice(0, 5),
        bot_type: "day", direction: "BUY",
        entry_price: "", stop_loss: "",
        take_profit_1: "", take_profit_2: "", take_profit_3: "",
        exit_price: "", lot_size: "0.2", duration_minutes: "", notes: "",
      });
    }
  }, [trade, open]);

  // Live preview of metrics
  const preview = useMemo(() => {
    const entry = parseFloat(form.entry_price);
    const sl = parseFloat(form.stop_loss);
    const exit = parseFloat(form.exit_price);
    const lot = parseFloat(form.lot_size) || 1;
    if (!entry || !sl) return null;
    const risk = Math.abs(entry - sl);
    if (!exit) return { risk_pts: risk.toFixed(1) };
    const points = form.direction === "BUY" ? exit - entry : entry - exit;
    const pnl = points * lot * 10;  // DAX40 pip value: 10€ per point per full lot
    const rr = risk > 0 ? (Math.abs(points) / risk) * (points >= 0 ? 1 : -1) : 0;
    return { points: points.toFixed(1), pnl: pnl.toFixed(2), rr: rr.toFixed(2), risk_pts: risk.toFixed(1) };
  }, [form]);

  const handleSubmit = async () => {
    if (!form.entry_price || !form.stop_loss || !form.trade_date || !form.trade_time) {
      toast.error("Champs obligatoires manquants");
      return;
    }
    const payload = {
      ...form,
      entry_price: parseFloat(form.entry_price),
      stop_loss: parseFloat(form.stop_loss),
      take_profit_1: form.take_profit_1 ? parseFloat(form.take_profit_1) : null,
      take_profit_2: form.take_profit_2 ? parseFloat(form.take_profit_2) : null,
      take_profit_3: form.take_profit_3 ? parseFloat(form.take_profit_3) : null,
      exit_price: form.exit_price ? parseFloat(form.exit_price) : null,
      lot_size: parseFloat(form.lot_size) || 1.0,
      duration_minutes: form.duration_minutes ? parseInt(form.duration_minutes) : null,
    };
    await onSubmit(payload);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl rounded-sm" data-testid="trade-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">
            {isEdit ? "Modifier le trade" : "Nouveau trade DAX40"}
          </DialogTitle>
          <DialogDescription className="font-body text-xs">
            1 point DAX = 1€ par lot. Les calculs (P&L, R/R, statut) sont automatiques.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 mt-2">
          <div>
            <Label className="text-xs font-body uppercase tracking-wider text-slate-500">Date</Label>
            <Input type="date" value={form.trade_date} data-testid="form-date"
                   onChange={(e) => setForm({ ...form, trade_date: e.target.value })}
                   className="rounded-sm font-data" />
          </div>
          <div>
            <Label className="text-xs font-body uppercase tracking-wider text-slate-500">Heure</Label>
            <Input type="time" value={form.trade_time} data-testid="form-time"
                   onChange={(e) => setForm({ ...form, trade_time: e.target.value })}
                   className="rounded-sm font-data" />
          </div>

          <div>
            <Label className="text-xs font-body uppercase tracking-wider text-slate-500">Bot</Label>
            <Select value={form.bot_type} onValueChange={(v) => setForm({ ...form, bot_type: v })}>
              <SelectTrigger className="rounded-sm" data-testid="form-bot"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="scalp">Scalping (M1/M5)</SelectItem>
                <SelectItem value="day">Day Trading (M15/H1)</SelectItem>
                <SelectItem value="swing">Swing (H4/D1)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-body uppercase tracking-wider text-slate-500">Direction</Label>
            <Select value={form.direction} onValueChange={(v) => setForm({ ...form, direction: v })}>
              <SelectTrigger className="rounded-sm" data-testid="form-direction"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="BUY">BUY (Long)</SelectItem>
                <SelectItem value="SELL">SELL (Short)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs font-body uppercase tracking-wider text-slate-500">Entrée</Label>
            <Input type="number" step="0.5" value={form.entry_price} data-testid="form-entry"
                   onChange={(e) => setForm({ ...form, entry_price: e.target.value })}
                   className="rounded-sm font-data" placeholder="18250.0" />
          </div>
          <div>
            <Label className="text-xs font-body uppercase tracking-wider text-slate-500">Stop Loss</Label>
            <Input type="number" step="0.5" value={form.stop_loss} data-testid="form-sl"
                   onChange={(e) => setForm({ ...form, stop_loss: e.target.value })}
                   className="rounded-sm font-data" placeholder="18220.0" />
          </div>

          <div>
            <Label className="text-xs font-body uppercase tracking-wider text-slate-500">TP1</Label>
            <Input type="number" step="0.5" value={form.take_profit_1}
                   onChange={(e) => setForm({ ...form, take_profit_1: e.target.value })}
                   className="rounded-sm font-data" placeholder="18290.0" />
          </div>
          <div>
            <Label className="text-xs font-body uppercase tracking-wider text-slate-500">TP2</Label>
            <Input type="number" step="0.5" value={form.take_profit_2}
                   onChange={(e) => setForm({ ...form, take_profit_2: e.target.value })}
                   className="rounded-sm font-data" placeholder="(optionnel)" />
          </div>
          <div>
            <Label className="text-xs font-body uppercase tracking-wider text-slate-500">TP3</Label>
            <Input type="number" step="0.5" value={form.take_profit_3}
                   onChange={(e) => setForm({ ...form, take_profit_3: e.target.value })}
                   className="rounded-sm font-data" placeholder="(optionnel)" />
          </div>
          <div>
            <Label className="text-xs font-body uppercase tracking-wider text-slate-500">Sortie</Label>
            <Input type="number" step="0.5" value={form.exit_price} data-testid="form-exit"
                   onChange={(e) => setForm({ ...form, exit_price: e.target.value })}
                   className="rounded-sm font-data" placeholder="(vide = ouvert)" />
          </div>

          <div>
            <Label className="text-xs font-body uppercase tracking-wider text-slate-500">Lot Size</Label>
            <Input type="number" step="0.1" value={form.lot_size}
                   onChange={(e) => setForm({ ...form, lot_size: e.target.value })}
                   className="rounded-sm font-data" />
          </div>
          <div>
            <Label className="text-xs font-body uppercase tracking-wider text-slate-500">Durée (min)</Label>
            <Input type="number" value={form.duration_minutes}
                   onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })}
                   className="rounded-sm font-data" placeholder="(optionnel)" />
          </div>

          <div className="col-span-2">
            <Label className="text-xs font-body uppercase tracking-wider text-slate-500">Notes</Label>
            <Textarea value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      className="rounded-sm font-body text-sm" rows={2}
                      placeholder="Setup, contexte, leçons..." />
          </div>
        </div>

        {/* Live preview */}
        {preview && (
          <div className="bg-slate-50 rounded-sm p-3 border border-slate-200 flex items-center justify-between flex-wrap gap-3 mt-2">
            <div className="text-xs">
              <span className="text-slate-400 uppercase tracking-wider font-body">Risque:</span>
              <span className="ml-1 font-data font-bold text-slate-900 tabular-nums">{preview.risk_pts} pts</span>
            </div>
            {preview.points !== undefined && (
              <>
                <div className="text-xs">
                  <span className="text-slate-400 uppercase tracking-wider font-body">Points:</span>
                  <span className={`ml-1 font-data font-bold tabular-nums ${parseFloat(preview.points) >= 0 ? 'text-[#1D9E75]' : 'text-[#D85A30]'}`}>
                    {fmtPts(preview.points)}
                  </span>
                </div>
                <div className="text-xs">
                  <span className="text-slate-400 uppercase tracking-wider font-body">P&L:</span>
                  <span className={`ml-1 font-data font-bold tabular-nums ${parseFloat(preview.pnl) >= 0 ? 'text-[#1D9E75]' : 'text-[#D85A30]'}`}>
                    {fmtEur(preview.pnl)}
                  </span>
                </div>
                <div className="text-xs">
                  <span className="text-slate-400 uppercase tracking-wider font-body">R/R:</span>
                  <span className={`ml-1 font-data font-bold tabular-nums ${parseFloat(preview.rr) >= 0 ? 'text-[#1D9E75]' : 'text-[#D85A30]'}`}>
                    {parseFloat(preview.rr).toFixed(2)}
                  </span>
                </div>
              </>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-sm">Annuler</Button>
          <Button onClick={handleSubmit} className="rounded-sm bg-[#378ADD] hover:bg-[#1F65B0]" data-testid="form-submit">
            {isEdit ? "Enregistrer" : "Ajouter le trade"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Performance rating from R:R
const ratingForRR = (rr) => {
  const r = Math.abs(rr);
  if (r >= 3) return { label: "Excellent", color: "#1D9E75", bg: "#1D9E7515", icon: "★★★" };
  if (r >= 2) return { label: "Bon", color: "#378ADD", bg: "#378ADD15", icon: "★★" };
  if (r >= 1) return { label: "Moyen", color: "#f59e0b", bg: "#f59e0b15", icon: "★" };
  return { label: "Faible", color: "#D85A30", bg: "#D85A3015", icon: "○" };
};

// ===== Trade Detail Dialog (read-only popup) =====
const TradeDetailDialog = ({ trade, open, onClose, onEdit, onDelete }) => {
  if (!trade) return null;
  const isWin = trade.status === "winner";
  const isLoss = trade.status === "loser";
  const isOpen = trade.status === "open";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl rounded-sm" data-testid="trade-detail-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-heading text-xl font-bold">DAX40</span>
            <Badge variant="outline" className={BOT_BADGE[trade.bot_type]?.className || ""}>
              {BOT_BADGE[trade.bot_type]?.label || trade.bot_type}
            </Badge>
            <Badge className={`font-data text-xs ${trade.direction === "BUY" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
              {trade.direction === "BUY" ? <ArrowUp size={12} weight="bold" className="mr-1 inline" /> : <ArrowDown size={12} weight="bold" className="mr-1 inline" />}
              {trade.direction}
            </Badge>
            {!isOpen && (
              <Badge className="font-data text-xs" style={{ background: isWin ? COLORS.profit : COLORS.loss, color: "white" }}>
                {isWin ? "GAGNANT" : isLoss ? "PERDANT" : "BREAKEVEN"}
              </Badge>
            )}
            {isOpen && <Badge variant="outline" className="font-data text-xs border-blue-300 text-blue-700">EN COURS</Badge>}
          </div>
          <DialogTitle className="sr-only">Détails du trade</DialogTitle>
          <DialogDescription className="font-body text-xs text-slate-400">
            {new Date(`${trade.trade_date}T${trade.trade_time}`).toLocaleString("fr-FR")} • Lot {trade.lot_size}
            {trade.duration_minutes ? ` • ${trade.duration_minutes >= 1440 ? Math.round(trade.duration_minutes / 1440) + 'j' : trade.duration_minutes + 'min'}` : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-3 mt-3">
          <div className="bg-slate-50 border border-slate-100 rounded-sm p-3">
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-body mb-1">Entrée</p>
            <p className="font-data text-base font-bold tabular-nums">{fmtPrice(trade.entry_price)}</p>
          </div>
          <div className="bg-red-50 border border-red-100 rounded-sm p-3">
            <p className="text-[10px] uppercase tracking-wider text-[#D85A30] font-body mb-1">Stop Loss</p>
            <p className="font-data text-base font-bold tabular-nums text-[#D85A30]">{fmtPrice(trade.stop_loss)}</p>
          </div>
          <div className="bg-emerald-50 border border-emerald-100 rounded-sm p-3">
            <p className="text-[10px] uppercase tracking-wider text-[#1D9E75] font-body mb-1">Sortie</p>
            <p className="font-data text-base font-bold tabular-nums text-[#1D9E75]">{fmtPrice(trade.exit_price)}</p>
          </div>

          <div className="bg-slate-50 border border-slate-100 rounded-sm p-3">
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-body mb-1">TP1 / TP2 / TP3</p>
            <p className="font-data text-sm font-medium tabular-nums">
              {fmtPrice(trade.take_profit_1)} / {fmtPrice(trade.take_profit_2)} / {fmtPrice(trade.take_profit_3)}
            </p>
          </div>
          <div className="bg-slate-50 border border-slate-100 rounded-sm p-3">
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-body mb-1">Points</p>
            <p className={`font-data text-base font-bold tabular-nums`} style={{ color: trade.points >= 0 ? COLORS.profit : COLORS.loss }}>
              {fmtPts(trade.points)}
            </p>
          </div>
          <div className="rounded-sm p-3 border" style={{ background: trade.pnl_eur >= 0 ? `${COLORS.profit}15` : `${COLORS.loss}15`, borderColor: trade.pnl_eur >= 0 ? `${COLORS.profit}40` : `${COLORS.loss}40` }}>
            <p className="text-[10px] uppercase tracking-wider font-body mb-1" style={{ color: trade.pnl_eur >= 0 ? COLORS.profit : COLORS.loss }}>P&L</p>
            <p className="font-data text-base font-bold tabular-nums" style={{ color: trade.pnl_eur >= 0 ? COLORS.profit : COLORS.loss }} data-testid="dialog-pnl">
              {fmtEur(trade.pnl_eur)}
            </p>
            <p className="text-[10px] text-slate-500 mt-1">R/R: {trade.risk_reward}</p>
          </div>
        </div>

        {trade.notes && (
          <div className="bg-blue-50 border border-blue-100 rounded-sm p-3 mt-3">
            <p className="text-[10px] uppercase tracking-wider text-[#378ADD] font-body mb-1">Notes</p>
            <p className="font-body text-sm text-slate-700">{trade.notes}</p>
          </div>
        )}

        {/* Performance rating (only for closed trades) */}
        {!isOpen && trade.risk_reward !== undefined && (() => {
          const r = ratingForRR(trade.risk_reward);
          return (
            <div className="rounded-sm border p-3 mt-3 flex items-center justify-between"
                 style={{ background: r.bg, borderColor: `${r.color}40` }}
                 data-testid="dialog-rating">
              <div>
                <p className="text-[10px] uppercase tracking-wider font-body" style={{ color: r.color }}>
                  Note de performance
                </p>
                <p className="font-heading text-base font-bold" style={{ color: r.color }}>
                  {r.icon} {r.label}
                </p>
              </div>
              <p className="font-data text-xs text-slate-500 tabular-nums">
                R/R réalisé: <span className="font-bold" style={{ color: r.color }}>{Math.abs(trade.risk_reward).toFixed(2)}</span>
              </p>
            </div>
          );
        })()}

        <DialogFooter className="gap-2 mt-3">
          <Button variant="outline" className="rounded-sm" onClick={() => onDelete(trade)} data-testid="dialog-delete">
            <Trash size={14} className="mr-1" />
            Supprimer
          </Button>
          <Button className="rounded-sm bg-[#378ADD] hover:bg-[#1F65B0]" onClick={() => onEdit(trade)} data-testid="dialog-edit">
            <Pencil size={14} className="mr-1" />
            Modifier
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ===== Pattern Card (auto-detected winning/losing pattern) =====
const PatternCard = ({ pattern }) => {
  const isWinning = pattern.kind === "winning";
  const Icon = isWinning ? TrendUp : TrendDown;
  const accentColor = isWinning ? COLORS.profit : COLORS.loss;
  const tagBg = isWinning ? `${COLORS.profit}15` : `${COLORS.loss}15`;
  const tagBorder = isWinning ? `${COLORS.profit}40` : `${COLORS.loss}40`;

  return (
    <div
      className="rounded-sm p-3 border bg-white hover:shadow-md transition-shadow flex items-start gap-3"
      style={{ borderColor: tagBorder }}
      data-testid={`pattern-${pattern.kind}`}
    >
      <div
        className="w-9 h-9 rounded-sm flex items-center justify-center shrink-0"
        style={{ background: tagBg, color: accentColor }}
      >
        <Icon size={16} weight="bold" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className="text-[10px] uppercase tracking-wider text-slate-400 font-body font-bold">
            {pattern.dimension}
          </span>
          <Badge
            variant="outline"
            className="text-[9px] font-data tabular-nums"
            style={{ borderColor: tagBorder, color: accentColor }}
          >
            {pattern.delta_pct >= 0 ? "+" : ""}{pattern.delta_pct} pp
          </Badge>
        </div>
        <p className="font-heading text-sm font-bold text-slate-900 mb-1 truncate">
          {pattern.label}
        </p>
        <div className="flex items-center gap-3 text-xs">
          <span className="text-slate-500 font-body">
            <span className="font-data font-bold tabular-nums" style={{ color: accentColor }}>
              {pattern.win_rate}%
            </span>
            {" "}win rate
          </span>
          <span className="text-slate-400 font-body">
            {pattern.count} trades ({pattern.wins}W/{pattern.losses}L)
          </span>
          <span
            className="font-data tabular-nums font-bold ml-auto"
            style={{ color: pattern.avg_pnl_eur >= 0 ? COLORS.profit : COLORS.loss }}
          >
            {fmtEur(pattern.avg_pnl_eur)}/trade
          </span>
        </div>
      </div>
    </div>
  );
};

// ===== Equity Curve Chart =====
const EquityCurve = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-12 text-slate-400 font-body text-sm">
        Aucune donnée d'équité (ajoutez ou clôturez des trades)
      </div>
    );
  }
  return (
    <ResponsiveContainer width="100%" height={240}>
      <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
        <defs>
          <linearGradient id="equityGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={COLORS.neutral} stopOpacity={0.4} />
            <stop offset="100%" stopColor={COLORS.neutral} stopOpacity={0.0} />
          </linearGradient>
        </defs>
        <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#64748b" }} stroke="#cbd5e1"
               tickFormatter={(v) => v ? v.slice(5, 10) : ""} />
        <YAxis tick={{ fontSize: 10, fill: "#64748b" }} stroke="#cbd5e1"
               tickFormatter={(v) => `${v}€`} />
        <Tooltip
          contentStyle={{ background: "white", border: "1px solid #e2e8f0", borderRadius: "2px", fontSize: "12px" }}
          formatter={(v) => [fmtEur(v), "P&L cumulé"]}
        />
        <ReferenceLine y={0} stroke="#cbd5e1" strokeDasharray="3 3" />
        <Area type="monotone" dataKey="pnl_eur" stroke={COLORS.neutral} strokeWidth={2}
              fill="url(#equityGrad)" />
      </AreaChart>
    </ResponsiveContainer>
  );
};

// ===== Main Page =====
export default function TradingJournal() {
  const [stats, setStats] = useState(null);
  const [trades, setTrades] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [patterns, setPatterns] = useState(null);
  const [loading, setLoading] = useState(true);

  const [filterBot, setFilterBot] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [searchNotes, setSearchNotes] = useState("");
  const [page, setPage] = useState(1);
  const PER_PAGE = 20;
  const [sortBy, setSortBy] = useState("pnl_eur");
  const [sortDir, setSortDir] = useState("desc");

  const [formOpen, setFormOpen] = useState(false);
  const [editingTrade, setEditingTrade] = useState(null);
  const [detailTrade, setDetailTrade] = useState(null);

  const fileInputRef = useRef(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ sort_by: sortBy, sort_dir: sortDir });
      if (filterBot !== "all") params.append("bot_type", filterBot);
      if (filterStatus !== "all") params.append("status", filterStatus);
      if (filterDateFrom) params.append("date_from", filterDateFrom);
      if (filterDateTo) params.append("date_to", filterDateTo);

      const [tradesRes, statsRes, alertsRes, patternsRes] = await Promise.all([
        fetch(`${API}/journal/trades?${params}`).then((r) => r.json()),
        fetch(`${API}/journal/stats`).then((r) => r.json()),
        fetch(`${API}/journal/alerts?capital=10000`).then((r) => r.json()),
        fetch(`${API}/journal/patterns?min_count=2&delta_threshold=15`).then((r) => r.json()),
      ]);
      setTrades(tradesRes);
      setStats(statsRes);
      setAlerts(alertsRes.alerts || []);
      setPatterns(patternsRes);
    } catch (err) {
      console.error(err);
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, [filterBot, filterStatus, filterDateFrom, filterDateTo, sortBy, sortDir]);

  // ===== CRUD handlers =====
  const handleCreate = async (payload) => {
    try {
      const url = editingTrade ? `${API}/journal/trades/${editingTrade.id}` : `${API}/journal/trades`;
      const method = editingTrade ? "PUT" : "POST";
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Erreur");
      toast.success(editingTrade ? "Trade modifié" : "Trade ajouté");
      setFormOpen(false);
      setEditingTrade(null);
      setDetailTrade(null);
      await loadAll();
    } catch {
      toast.error("Échec de l'enregistrement");
    }
  };

  const handleDelete = async (trade) => {
    if (!window.confirm(`Supprimer ce trade ${trade.bot_type.toUpperCase()} du ${trade.trade_date} ?`)) return;
    try {
      await fetch(`${API}/journal/trades/${trade.id}`, { method: "DELETE" });
      toast.success("Trade supprimé");
      setDetailTrade(null);
      await loadAll();
    } catch {
      toast.error("Erreur");
    }
  };

  const handleSeed = async () => {
    if (!window.confirm("Réinitialiser et charger 10 trades d'exemple ?")) return;
    try {
      await fetch(`${API}/journal/seed?reset=true`, { method: "POST" });
      toast.success("10 trades d'exemple chargés");
      await loadAll();
    } catch {
      toast.error("Erreur seed");
    }
  };

  const handleExport = async () => {
    try {
      const res = await fetch(`${API}/journal/export`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `journal_dax40_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export CSV téléchargé");
    } catch {
      toast.error("Erreur export");
    }
  };

  const handleExportPdf = async () => {
    try {
      const now = new Date();
      const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
      toast.info("Génération du PDF...");
      const res = await fetch(`${API}/journal/export-pdf?month=${month}`);
      if (!res.ok) throw new Error("PDF generation failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `dax_pro_report_${month}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Rapport PDF téléchargé");
    } catch {
      toast.error("Erreur génération PDF");
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await fetch(`${API}/journal/import`, { method: "POST", body: fd });
      const result = await res.json();
      toast.success(`${result.inserted} trades importés (${result.skipped} ignorés)`);
      await loadAll();
    } catch {
      toast.error("Erreur d'import");
    }
    e.target.value = "";
  };

  // Apply client-side search by notes (server already handles other filters)
  const filteredTrades = useMemo(() => {
    if (!searchNotes.trim()) return trades;
    const q = searchNotes.toLowerCase();
    return trades.filter(t => (t.notes || "").toLowerCase().includes(q));
  }, [trades, searchNotes]);

  const totalPages = Math.max(1, Math.ceil(filteredTrades.length / PER_PAGE));
  const pageTrades = filteredTrades.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  useEffect(() => { setPage(1); }, [filterBot, filterStatus, filterDateFrom, filterDateTo, searchNotes]);

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortDir(sortDir === "desc" ? "asc" : "desc");
    } else {
      setSortBy(field);
      setSortDir("desc");
    }
  };

  return (
    <div className="container mx-auto px-4 lg:px-8 py-6 max-w-[1400px]" data-testid="trading-journal-page">
      <DaxLiveHeader />

      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Journal de Trading DAX40</h1>
          <p className="text-sm text-slate-500 font-body mt-1">
            Suivi professionnel des 3 bots — Scalping, Day Trading, Swing
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" className="rounded-sm" onClick={handleSeed} data-testid="btn-seed">
            <ArrowsClockwise size={14} className="mr-1" />
            Données démo
          </Button>
          <Button variant="outline" size="sm" className="rounded-sm" onClick={() => fileInputRef.current?.click()} data-testid="btn-import">
            <UploadSimple size={14} className="mr-1" />
            Importer CSV
          </Button>
          <input ref={fileInputRef} type="file" accept=".csv" hidden onChange={handleImport} />
          <Button variant="outline" size="sm" className="rounded-sm" onClick={handleExport} data-testid="btn-export">
            <DownloadSimple size={14} className="mr-1" />
            Exporter CSV
          </Button>
          <Button variant="outline" size="sm" className="rounded-sm bg-[#378ADD]/5 hover:bg-[#378ADD]/10 border-[#378ADD]/40 text-[#1F65B0]" onClick={handleExportPdf} data-testid="btn-export-pdf">
            <DownloadSimple size={14} className="mr-1" />
            Rapport PDF
          </Button>
          <Button size="sm" className="rounded-sm bg-[#1D9E75] hover:bg-[#156c50] text-white"
                  onClick={() => { setEditingTrade(null); setFormOpen(true); }} data-testid="btn-new-trade">
            <Plus size={14} className="mr-1" />
            Nouveau trade
          </Button>
        </div>
      </div>

      {/* ALERTS BANNER */}
      {alerts.length > 0 && (
        <div className="mb-4 space-y-2">
          {alerts.map((a, i) => (
            <div
              key={i}
              className={`rounded-sm border p-3 flex items-start gap-3 ${
                a.severity === "high" ? "bg-red-50 border-red-200" :
                a.severity === "medium" ? "bg-amber-50 border-amber-200" :
                "bg-blue-50 border-blue-200"
              }`}
              data-testid={`alert-${a.type}`}
            >
              <Warning size={18} weight="fill" className={
                a.severity === "high" ? "text-red-600" :
                a.severity === "medium" ? "text-amber-600" : "text-blue-600"
              } />
              <div className="flex-1">
                <p className="font-heading text-sm font-bold text-slate-900">{a.title}</p>
                <p className="font-body text-xs text-slate-600 mt-0.5">{a.message}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* HERO STATS GRID */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-6">
          <StatCard
            icon={TrendUp}
            label="P&L Total"
            value={fmtEur(stats.total_pnl_eur)}
            sub={`${stats.total_points >= 0 ? '+' : ''}${stats.total_points.toFixed(1)} pts`}
            accent={stats.total_pnl_eur >= 0 ? "profit" : "loss"}
            testid="stat-total-pnl"
          />
          <StatCard
            icon={Trophy}
            label="Win Rate"
            value={`${stats.win_rate}%`}
            sub={`${stats.winners}W / ${stats.losers}L`}
            accent={stats.win_rate >= 50 ? "profit" : "loss"}
            testid="stat-winrate"
          />
          <StatCard
            icon={ArrowUp}
            label="Meilleur"
            value={fmtEur(stats.best_trade?.pnl_eur)}
            sub={stats.best_trade?.bot_type?.toUpperCase()}
            accent="profit"
            testid="stat-best"
          />
          <StatCard
            icon={ArrowDown}
            label="Pire"
            value={fmtEur(stats.worst_trade?.pnl_eur)}
            sub={stats.worst_trade?.bot_type?.toUpperCase()}
            accent="loss"
            testid="stat-worst"
          />
          <StatCard
            icon={Target}
            label="R/R Moyen"
            value={stats.avg_risk_reward.toFixed(2)}
            sub="par trade"
            accent={stats.avg_risk_reward >= 1 ? "profit" : "neutral"}
            testid="stat-rr"
          />
          <StatCard
            icon={Lightning}
            label="Streak"
            value={`${stats.current_streak.kind === "win" ? "+" : stats.current_streak.kind === "loss" ? "-" : ""}${stats.current_streak.count}`}
            sub={`max ${stats.max_win_streak}W / ${stats.max_loss_streak}L`}
            accent={stats.current_streak.kind === "win" ? "profit" : stats.current_streak.kind === "loss" ? "loss" : "neutral"}
            testid="stat-streak"
          />
          <StatCard
            icon={TrendDown}
            label="Max DD"
            value={fmtEur(-stats.max_drawdown_eur)}
            sub="historique"
            accent="loss"
            testid="stat-dd"
          />
          <StatCard
            icon={ChartBar}
            label="Total"
            value={String(stats.total_trades)}
            sub={`${stats.open_trades} ouverts`}
            accent="neutral"
            testid="stat-total"
          />
        </div>
      )}

      {/* P&L BY BOT */}
      {stats?.by_bot && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
          {["scalp", "day", "swing"].map((bot) => {
            const b = stats.by_bot[bot] || { trades: 0, pnl_eur: 0, points: 0, wins: 0, win_rate: 0 };
            const positive = b.pnl_eur >= 0;
            return (
              <Card key={bot} className="rounded-sm border-slate-200 shadow-none" data-testid={`bot-card-${bot}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <Badge variant="outline" className={`${BOT_BADGE[bot]?.className} text-xs uppercase tracking-wider font-bold`}>
                      {BOT_BADGE[bot]?.label}
                    </Badge>
                    <span className="text-xs text-slate-400 font-body">{b.trades} trades</span>
                  </div>
                  <p className="font-data text-2xl font-bold tabular-nums" style={{ color: positive ? COLORS.profit : COLORS.loss }}>
                    {fmtEur(b.pnl_eur)}
                  </p>
                  <div className="flex items-center justify-between mt-2 text-xs text-slate-500">
                    <span className="font-body">{b.points >= 0 ? '+' : ''}{b.points.toFixed(1)} pts</span>
                    <span className="font-body">Win {b.win_rate}%</span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* PATTERNS DETECTION */}
      {patterns && patterns.patterns && patterns.patterns.length > 0 && (
        <Card className="rounded-sm border-slate-200 shadow-none mb-6" data-testid="patterns-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="font-heading text-sm font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                <Sparkle size={14} weight="fill" className="text-amber-500" />
                Patterns détectés automatiquement
              </CardTitle>
              <span className="text-xs font-body text-slate-400">
                Win rate global : <span className="font-data font-bold text-slate-700">{patterns.global_win_rate}%</span> sur {patterns.global_count} trades
              </span>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {patterns.patterns.slice(0, 8).map((p, i) => (
                <PatternCard key={i} pattern={p} />
              ))}
            </div>
            {patterns.patterns.length > 8 && (
              <p className="text-xs text-slate-400 mt-3 font-body">
                + {patterns.patterns.length - 8} autres patterns. Ajoutez plus de trades pour affiner.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* EQUITY CURVE */}
      {stats && (
        <Card className="rounded-sm border-slate-200 shadow-none mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-sm font-bold uppercase tracking-wider text-slate-500">
              Courbe d'équité
            </CardTitle>
          </CardHeader>
          <CardContent>
            <EquityCurve data={stats.equity_curve} />
          </CardContent>
        </Card>
      )}

      {/* FILTERS + TABLE */}
      <Card className="rounded-sm border-slate-200 shadow-none">
        <CardHeader className="pb-3">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <CardTitle className="font-heading text-sm font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
              <Funnel size={14} />
              Trades ({filteredTrades.length}{filteredTrades.length !== trades.length ? ` / ${trades.length}` : ""})
            </CardTitle>
            <div className="flex flex-wrap gap-2 items-center">
              <div className="relative">
                <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Recherche notes..."
                  value={searchNotes}
                  onChange={(e) => setSearchNotes(e.target.value)}
                  className="pl-7 w-44 rounded-sm h-8 text-xs"
                  data-testid="search-notes"
                />
              </div>
              <Select value={filterBot} onValueChange={setFilterBot}>
                <SelectTrigger className="w-32 rounded-sm h-8 text-xs" data-testid="filter-bot">
                  <SelectValue placeholder="Bot" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les bots</SelectItem>
                  <SelectItem value="scalp">Scalp</SelectItem>
                  <SelectItem value="day">Day</SelectItem>
                  <SelectItem value="swing">Swing</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-32 rounded-sm h-8 text-xs" data-testid="filter-status">
                  <SelectValue placeholder="Résultat" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  <SelectItem value="winner">Gagnants</SelectItem>
                  <SelectItem value="loser">Perdants</SelectItem>
                  <SelectItem value="open">Ouverts</SelectItem>
                </SelectContent>
              </Select>
              <Input type="date" value={filterDateFrom}
                     onChange={(e) => setFilterDateFrom(e.target.value)}
                     placeholder="Du" className="w-36 rounded-sm h-8 text-xs font-data" data-testid="filter-date-from" />
              <Input type="date" value={filterDateTo}
                     onChange={(e) => setFilterDateTo(e.target.value)}
                     placeholder="Au" className="w-36 rounded-sm h-8 text-xs font-data" data-testid="filter-date-to" />
              {(filterBot !== "all" || filterStatus !== "all" || filterDateFrom || filterDateTo) && (
                <Button variant="ghost" size="sm" className="rounded-sm h-8 text-xs"
                        onClick={() => { setFilterBot("all"); setFilterStatus("all"); setFilterDateFrom(""); setFilterDateTo(""); }}>
                  <X size={12} className="mr-1" /> Reset
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-400 font-body text-sm">Chargement…</div>
          ) : trades.length === 0 ? (
            <div className="p-8 text-center">
              <Calendar size={36} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500 font-body text-sm">Aucun trade — ajoutez-en un ou chargez des données démo</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/50">
                  <SortableHead label="Date" field="trade_date" current={sortBy} dir={sortDir} onClick={toggleSort} />
                  <SortableHead label="Heure" field="trade_time" current={sortBy} dir={sortDir} onClick={toggleSort} />
                  <SortableHead label="Bot" field="bot_type" current={sortBy} dir={sortDir} onClick={toggleSort} />
                  <TableHead className="text-xs uppercase tracking-wider font-body">Sens</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-body">Entrée</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-body">Sortie</TableHead>
                  <SortableHead label="Lot" field="lot_size" current={sortBy} dir={sortDir} onClick={toggleSort} />
                  <SortableHead label="Points" field="points" current={sortBy} dir={sortDir} onClick={toggleSort} />
                  <SortableHead label="P&L" field="pnl_eur" current={sortBy} dir={sortDir} onClick={toggleSort} />
                  <SortableHead label="R/R" field="risk_reward" current={sortBy} dir={sortDir} onClick={toggleSort} />
                  <TableHead className="text-xs uppercase tracking-wider font-body">Statut</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageTrades.map((t) => {
                  const bg = t.status === "winner" ? "hover:bg-emerald-50/60 bg-emerald-50/20" :
                             t.status === "loser" ? "hover:bg-red-50/60 bg-red-50/20" :
                             "hover:bg-blue-50/60";
                  return (
                    <TableRow
                      key={t.id}
                      className={`cursor-pointer transition-colors ${bg}`}
                      onClick={() => setDetailTrade(t)}
                      data-testid={`journal-row-${t.id}`}
                    >
                      <TableCell className="font-data tabular-nums text-xs text-slate-600">{t.trade_date}</TableCell>
                      <TableCell className="font-data tabular-nums text-xs text-slate-500">{t.trade_time}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`${BOT_BADGE[t.bot_type]?.className} text-[10px] font-bold`}>
                          {BOT_BADGE[t.bot_type]?.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className={`flex items-center gap-1 text-xs font-bold ${t.direction === "BUY" ? "text-[#1D9E75]" : "text-[#D85A30]"}`}>
                          {t.direction === "BUY" ? <ArrowUp size={11} weight="bold" /> : <ArrowDown size={11} weight="bold" />}
                          {t.direction}
                        </span>
                      </TableCell>
                      <TableCell className="font-data tabular-nums text-sm">{fmtPrice(t.entry_price)}</TableCell>
                      <TableCell className="font-data tabular-nums text-sm">{fmtPrice(t.exit_price)}</TableCell>
                      <TableCell className="font-data tabular-nums text-xs text-slate-500">{t.lot_size}</TableCell>
                      <TableCell className="font-data tabular-nums font-medium" style={{ color: (t.points || 0) >= 0 ? COLORS.profit : COLORS.loss }}>
                        {t.status === "open" ? "—" : fmtPts(t.points)}
                      </TableCell>
                      <TableCell className="font-data tabular-nums font-bold" style={{ color: (t.pnl_eur || 0) >= 0 ? COLORS.profit : COLORS.loss }}>
                        {t.status === "open" ? "—" : fmtEur(t.pnl_eur)}
                      </TableCell>
                      <TableCell className="font-data tabular-nums text-xs">
                        {t.status === "open" ? "—" : t.risk_reward.toFixed(2)}
                      </TableCell>
                      <TableCell>
                        {t.status === "winner" ? (
                          <Badge className="text-[10px] font-bold" style={{ background: COLORS.profit, color: "white" }}>GAGNANT</Badge>
                        ) : t.status === "loser" ? (
                          <Badge className="text-[10px] font-bold" style={{ background: COLORS.loss, color: "white" }}>PERDANT</Badge>
                        ) : t.status === "open" ? (
                          <Badge variant="outline" className="text-[10px] font-bold border-blue-300 text-blue-700">OUVERT</Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px]">BE</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}

          {/* Pagination */}
          {filteredTrades.length > PER_PAGE && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
              <span className="text-xs text-slate-500 font-body">
                Page <span className="font-data font-bold">{page}</span> sur <span className="font-data font-bold">{totalPages}</span>
                {" "}— {(page - 1) * PER_PAGE + 1}-{Math.min(page * PER_PAGE, filteredTrades.length)} sur {filteredTrades.length}
              </span>
              <div className="flex gap-1" data-testid="pagination">
                <Button variant="outline" size="sm" className="rounded-sm h-7 text-xs" disabled={page === 1}
                        onClick={() => setPage(p => Math.max(1, p - 1))}>← Précédent</Button>
                <Button variant="outline" size="sm" className="rounded-sm h-7 text-xs" disabled={page === totalPages}
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}>Suivant →</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* DIALOGS */}
      <TradeFormDialog
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditingTrade(null); }}
        onSubmit={handleCreate}
        trade={editingTrade}
      />
      <TradeDetailDialog
        trade={detailTrade}
        open={!!detailTrade}
        onClose={() => setDetailTrade(null)}
        onEdit={(t) => { setDetailTrade(null); setEditingTrade(t); setFormOpen(true); }}
        onDelete={handleDelete}
      />
    </div>
  );
}

// Sortable column header
const SortableHead = ({ label, field, current, dir, onClick }) => (
  <TableHead
    className="text-xs uppercase tracking-wider font-body cursor-pointer hover:bg-slate-100 transition-colors select-none"
    onClick={() => onClick(field)}
    data-testid={`sort-${field}`}
  >
    <span className="inline-flex items-center gap-1">
      {label}
      {current === field && (dir === "desc" ? <ArrowDown size={10} weight="bold" /> : <ArrowUp size={10} weight="bold" />)}
    </span>
  </TableHead>
);
